// 009 Álbum compartido — hooks del ciclo del invitado y de la moderación de la pareja.
// El invitado (anónimo) accede SOLO por RPC con access_code; la pareja (auth) por RLS de owner.
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Techo de seguridad para el ARCHIVO ORIGINAL (antes de comprimir). Alto a propósito:
// las fotos de boda profesionales pesan 8-20 MB. El bucket las acepta; nosotros las
// redimensionamos en el cliente antes de subir, así que este límite es solo un guardarraíl.
const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30 MB
// Lado máximo y calidad del JPEG resultante tras redimensionar (buena calidad de visualización
// sin llenar el storage con originales gigantes).
const MAX_DIMENSION = 2000;
const JPEG_QUALITY = 0.85;
// Incluye heic/heif (fotos de iPhone): su file.type suele venir vacío, así que validamos
// por extensión además de por MIME.
const VALID_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];

/**
 * Redimensiona/comprime una imagen en el navegador a un JPEG (lado ≤ MAX_DIMENSION).
 * Devuelve null si el navegador no puede decodificar el formato (p. ej. HEIC en Chrome),
 * para que el llamador suba el original como fallback.
 */
async function downscaleToJpeg(file: File): Promise<Blob | null> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement | null>((resolve) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => resolve(null);
      im.src = url;
    });
    if (!img || !img.width || !img.height) return null;

    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export interface AlbumPhoto {
  id: string;
  photo_url: string;
  caption: string | null;
  created_at: string;
}

/** Foto con estado y atribución — SOLO para la vista de la pareja (owner/RLS). */
export interface OwnerAlbumPhoto extends AlbumPhoto {
  guest_id: string | null;
  is_approved: boolean;
  is_hidden: boolean;
}

// ─── US3: el invitado ve el álbum (aprobadas ∧ ¬ocultas), sin autoría (C1) ───
export function useAlbumPhotos(accessCode: string) {
  return useQuery({
    queryKey: ['album-photos', accessCode],
    enabled: Boolean(accessCode),
    queryFn: async (): Promise<AlbumPhoto[]> => {
      const { data, error } = await supabase.rpc('get_album_photos', { p_access_code: accessCode });
      if (error) throw error;
      return (data ?? []) as unknown as AlbumPhoto[];
    },
  });
}

// ─── US1: el invitado sube (bucket + RPC con tope/reemplazo) ───
export interface UploadResult {
  photo: { id: string; photo_url: string; caption: string | null; is_approved: boolean; created_at: string };
  replaced_url: string | null;
}

export function useUploadGuestPhoto(accessCode: string, invitationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, caption }: { file: File; caption?: string }): Promise<UploadResult> => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      // Válida si el MIME es imagen O la extensión está permitida (HEIC llega con type vacío).
      const okType = file.type.startsWith('image/') || (ext ? VALID_EXTENSIONS.includes(ext) : false);
      if (!okType) {
        throw new Error('La foto debe ser una imagen (jpg, png, webp, gif o heic).');
      }
      if (file.size > MAX_FILE_SIZE) {
        throw new Error('La imagen es demasiado grande (máx. 30 MB).');
      }

      // Redimensiona/comprime en el cliente (fotos de boda profesionales pesan mucho).
      // Si el navegador no puede decodificar (p. ej. HEIC en Chrome), sube el original.
      const compressed = await downscaleToJpeg(file);
      const body: Blob = compressed ?? file;
      const uploadExt = compressed ? 'jpg' : (ext ?? 'jpg');
      const contentType = compressed ? 'image/jpeg' : (file.type || 'application/octet-stream');

      // Ruta exigida por la RLS de subida anónima (005): guest-uploads/<invitationId>/<archivo>.
      const fileName = `guest-uploads/${invitationId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${uploadExt}`;
      const { error: uploadError } = await supabase.storage
        .from('invitation-photos')
        .upload(fileName, body, { contentType });
      if (uploadError) {
        throw new Error(`Storage: ${uploadError.message || 'no se pudo subir el archivo'}`);
      }

      const { data: urlData } = supabase.storage.from('invitation-photos').getPublicUrl(fileName);
      const { data, error } = await supabase.rpc('upload_guest_photo', {
        p_access_code: accessCode,
        p_photo_url: urlData.publicUrl,
        p_caption: caption || undefined,
      });
      if (error) {
        // Mapea los errores del RPC (RAISE EXCEPTION) a mensajes accionables.
        const raw = error.message || '';
        if (raw.includes('invalid_access_code')) {
          throw new Error('Tu enlace de invitado no es válido para subir fotos (código no reconocido).');
        }
        if (raw.includes('album_unavailable')) {
          throw new Error('El álbum no está disponible para esta invitación.');
        }
        throw new Error(`RPC: ${raw || 'no se pudo registrar la foto'}`);
      }
      // Nota storage (U1): el objeto reemplazado (replaced_url) NO lo borra el invitado anónimo;
      // queda para la purga del owner (ver T030). Aquí solo informamos el reemplazo.
      return data as unknown as UploadResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['album-photos', accessCode] });
    },
  });
}

// ─── US1: fake-door del upsell (registra intención, no cobra ni habilita) ───
export function useRecordUpgradeIntent(accessCode: string) {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const { error } = await supabase.rpc('record_album_upgrade_intent', { p_access_code: accessCode });
      if (error) throw error;
    },
  });
}

// ─── US4: la pareja ve TODAS las fotos con estado y atribución (RLS de owner) ───
export function useAllGuestPhotos(invitationId: string) {
  return useQuery({
    queryKey: ['owner-album', invitationId],
    enabled: Boolean(invitationId),
    queryFn: async (): Promise<OwnerAlbumPhoto[]> => {
      const { data, error } = await supabase
        .from('guest_photos')
        .select('id, photo_url, caption, created_at, guest_id, is_approved, is_hidden')
        .eq('invitation_id', invitationId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map((r) => ({
        id: r.id,
        photo_url: r.photo_url,
        caption: r.caption,
        created_at: r.created_at ?? '',
        guest_id: r.guest_id,
        is_approved: r.is_approved ?? false,
        is_hidden: r.is_hidden ?? false,
      }));
    },
  });
}

// ─── US2: moderación 1-a-1 (aprobar / ocultar-mostrar) por RLS de owner ───
export function useModerateGuestPhoto(invitationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: { is_approved?: boolean; is_hidden?: boolean } }) => {
      const { error } = await supabase.from('guest_photos').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owner-album', invitationId] });
      toast.success('Foto actualizada');
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

// ─── US2: moderación en lote ("aprobar todo" / "ocultar todo") ───
export function useModerateAlbumBatch(invitationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: { is_approved?: boolean; is_hidden?: boolean }) => {
      const { error } = await supabase.from('guest_photos').update(patch).eq('invitation_id', invitationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owner-album', invitationId] });
      toast.success('Álbum actualizado');
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

// ─── US2: eliminar permanente (fila + objeto de storage; owner tiene DELETE, T006) ───
export function useDeleteGuestPhoto(invitationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, photoUrl }: { id: string; photoUrl: string }) => {
      const { error } = await supabase.from('guest_photos').delete().eq('id', id);
      if (error) throw error;
      // Purga del objeto de storage (irreversible). El path es guest-uploads/<inv>/<archivo>.
      const marker = '/invitation-photos/';
      const idx = photoUrl.indexOf(marker);
      if (idx !== -1) {
        const objectPath = photoUrl.slice(idx + marker.length);
        await supabase.storage.from('invitation-photos').remove([objectPath]);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owner-album', invitationId] });
      toast.success('Foto eliminada');
    },
    onError: (error: Error) => toast.error(error.message),
  });
}
